import React, { useState, useEffect } from 'react';
import { useSetCryptoOnboardingComplete, useSetOnboardingStage, useOnboardingStage, useStripeOnboardingStatus } from '../state';
import fetchWithToken, { updateOnboardingStage } from '../services';
import Loading from './Loading';
import PageLayout from './PageLayout';
import { useNavigate } from 'react-router-dom';
import { ONBOARDING_ROUTES, GENERAL_ROUTES } from '../constants/routes';
import { Button, init, requestProvider, onConnected } from '@getalby/bitcoin-connect-react';

// Initialize Bitcoin Connect
init({
  appName: 'WebGems',
  showBalance: true,
  providerConfig: {
    nwc: {
      authorizationUrlOptions: {
        requestMethods: ['get_balance', 'make_invoice', 'lookup_invoice'],
      },
    },
  }
});

const CryptoOnboarding = ({ setShowCryptoOnboarding, setShowStripeOnboarding }) => {
    const setCryptoOnboardingComplete = useSetCryptoOnboardingComplete();
    const setOnboardingStage = useSetOnboardingStage();
    const onboardingStage = useOnboardingStage();
    const [isLoading, setIsLoading] = useState(false);
    const [lightningAddress, setLightningAddress] = useState('');
    const [lightningAddressValid, setLightningAddressValid] = useState(false);
    const [error, setError] = useState(null);

    // start of bitcoin connect code
    const [connectedWallet, setConnectedWallet] = useState(null);
    
    // Utility function to log wallet permissions
    const logWalletPermissions = (provider) => {
        console.log('Bitcoin wallet connected successfully!');
        console.log('Provider:', provider);
        
        // Checking which methods are available on the provider
        const supportedMethods = [
            'getInfo',
            'makeInvoice',
            'sendPayment',
            'sendKeysend',
            'signMessage',
            'getBalance',
            'lookupInvoice'
        ];
        
        const availableMethods = {};
        supportedMethods.forEach(method => {
            availableMethods[method] = typeof provider[method] === 'function';
        });
        
        console.log('Granted permissions:', availableMethods);
        
        // Try to get additional info if available
        if (provider.getInfo && typeof provider.getInfo === 'function') {
            provider.getInfo().then(info => {
                console.log('Wallet Info:', info);
            }).catch(err => {
                console.log('Unable to get wallet info:', err.message);
            });
        }
        
        // Try to get balance if permission is granted
        if (provider.getBalance && typeof provider.getBalance === 'function') {
            provider.getBalance().then(balance => {
                console.log('Wallet Balance:', balance);
            }).catch(err => {
                console.log('Unable to get balance:', err.message);
            });
        }
    };
// end of bitcoin connect code

    const stripeOnboardingStatus = useStripeOnboardingStatus();
    const navigate = useNavigate();

    const validateLightningAddress = (address) => {
        // very basic validation, does not check if the address is actually valid
        // per https://github.com/lightningnetwork/lightning-rfc/blob/master/11-payment-encoding.md
        // const regex = /^ln[a-z0-9]+$/;
        const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

        return regex.test(address);
    };

    const handleChange = async (e) => {
        setLightningAddress(e.target.value)
    }

    useEffect(() => {
        if (validateLightningAddress(lightningAddress)) {
            setLightningAddressValid(true);
        } else {
            setLightningAddressValid(false);
        }
        console.log(lightningAddressValid);
    }, [lightningAddress]);
    
    // Setup global event listener for wallet connections
    useEffect(() => {
        console.log('Setting up Bitcoin Connect onConnected event listener');
        
        const unsubscribe = onConnected((provider) => {
            console.log('Global onConnected event triggered');
            setConnectedWallet(provider);
            logWalletPermissions(provider);
        });
        
        // Check if a wallet is already connected
        requestProvider({ enableOverride: false })
            .then(provider => {
                if (provider) {
                    console.log('Wallet already connected on component mount');
                    setConnectedWallet(provider);
                    logWalletPermissions(provider);
                }
            })
            .catch(err => {
                console.log('No wallet connected yet:', err.message);
            });
        
        return () => {
            // Clean up the event listener on unmount
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, []);

    const updateLightningAddress = async (lightningAddress) => {
        try {
            const response = await fetchWithToken('/update-lightning-address', {
                method: 'PATCH',
                body: JSON.stringify({ lightningAddress }),
            });

            if (!response.ok) {
                throw new Error('Failed to update lightning address');
            }
        } catch (error) {
            console.error('Error updating lightning address:', error);
            setError('Error updating lightning address. Please try again.');
        }
    }

    const createSpeedAccount = async () => {
        try {
            const response = await fetchWithToken('/create-speed-account', {
                method: 'POST'
            });

            if (!response.ok) {
                throw new Error('Failed to create Speed account');
            }
        } catch (error) {
            console.error('Error creating Speed account:', error);
            setError('Error creating Speed account. Please try again.');
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        // If using a connected wallet, we can proceed without a lightning address
        // Otherwise, validate the lightning address
        if (!connectedWallet && !validateLightningAddress(lightningAddress)) {
            setError('Invalid lightning address');
            setIsLoading(false);
            return;
        }

        try {
            // Only update lightning address if user provided one
            if (lightningAddress) {
                await updateLightningAddress(lightningAddress);
            }
            
            // If we have a connected wallet, we could store some information about it here
            // This is optional and depends on your backend implementation
            if (connectedWallet) {
                console.log('Connected wallet ready for use');
            }
        } catch (error) {
            console.error('Error updating wallet information:', error);
            setError('Error updating wallet information. Please try again.');
            setIsLoading(false);
            return;
        }

        try {
            await createSpeedAccount();
        } catch (error) {
            console.error('Error creating Speed account:', error);
            setError('Error creating Speed account. Please try again.');
            setIsLoading(false);
            return;
        }

        setCryptoOnboardingComplete(true);

        try {
            if (onboardingStage === 'crypto-onboarding') {
                await updateOnboardingStage('complete');
                setOnboardingStage('complete');
                navigate(GENERAL_ROUTES.HOME);
            } else if (onboardingStage === 'fiat-crypto-onboarding') {
                if (stripeOnboardingStatus === 'complete') {
                    await updateOnboardingStage('complete');
                    setOnboardingStage('complete');
                    navigate(GENERAL_ROUTES.HOME);
                } else {
                    navigate(ONBOARDING_ROUTES.FIAT_CRYPTO_ONBOARDING);
                }
            }
        } catch (error) {
            console.error('Error updating onboarding stage:', error);
            setError('Error updating onboarding stage. Please try again.');
            setIsLoading(false);
            navigate(GENERAL_ROUTES.ERROR);
        }

        setIsLoading(false);
    };

    const handleBack = async () => {
        setIsLoading(true);
        try {
            if (onboardingStage === 'fiat-crypto-onboarding') {
                // If we came from fiat-crypto-onboarding, just update the UI state to show the selection screen
                setShowCryptoOnboarding(false);
                setShowStripeOnboarding(false);
            } else {
                // Otherwise, go back to select-money-type
                await updateOnboardingStage('select-money-type');
                setOnboardingStage('select-money-type');
                navigate(ONBOARDING_ROUTES.SELECT_MONEY_TYPE);
            }
        } catch (error) {
            console.error('Error going back:', error);
            navigate(GENERAL_ROUTES.ERROR);
        }
        setIsLoading(false);
    };

    if (isLoading) {
        return <Loading />;
    }

    return (
        <PageLayout>
            <div className="flex flex-col items-center justify-center min-h-[calc(100vh)]">
                <div className="w-full max-w-md">
                    {/* start of new code */}
                    <div className="mb-8 text-center">
                        <h2 className="text-xl font-bold text-white mb-3">Connect Your Bitcoin Lightning Wallet</h2>
                        <div className="flex justify-center mb-6">
                            <Button 
                                onConnect={(provider) => {
                                    console.log('Button onConnect callback triggered');
                                    setConnectedWallet(provider);
                                    logWalletPermissions(provider);
                                }}
                            />
                        </div>
                        {connectedWallet && (
                            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded-md mb-4">
                                <p className="mb-2">Wallet connected successfully!</p>
                                <button 
                                    onClick={() => navigate('/bitcoin-test')} 
                                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-300 mt-2"
                                >
                                    Test Bitcoin Integration
                                </button>
                            </div>
                        )}
                    </div>
                    
                    <div className="text-center text-white mb-4">- OR -</div>
                    {/* end of new code */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="lightningAddress" className="block text-sm font-medium text-white mb-3 text-center">
                                Enter your Bitcoin Lightning address:
                            </label>
                            <input
                                type="text"
                                id="lightningAddress"
                                value={lightningAddress}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-1"
                                placeholder="you@lightning.address"
                                disabled={isLoading}
                            />
                            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                        </div>
                        <div className="flex justify-center gap-4 mt-4">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-300"
                                disabled={isLoading}
                            >
                                Back
                            </button>
                            <button
                                type="submit"
                                className={`px-4 py-2 rounded-md text-white font-medium ${(lightningAddressValid || connectedWallet) && !isLoading
                                    ? 'bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-300'
                                    : 'bg-gray-500 text-white px-4 py-2 rounded-md cursor-not-allowed'
                                    }`}
                                disabled={!(lightningAddressValid || connectedWallet) || isLoading}
                            >
                                {isLoading ? 'Setting up...' : 'Next'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </PageLayout>
    );
};

export default CryptoOnboarding;
